import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";
import { User, UserStatusType } from "../../store/auth";

export enum TechnicianStatus {
  INACTIVE = "inactive",
  ACTIVE = "active",
}
export enum COPSStatusDisplay {
  "Matched" = "Matched",
  "Not found" = "Not found",
}

export interface FieldTechnician {
  id: string;
  createdAt: string;
  updatedAt: string;
  COPSMatch: "Matched" | string;
  user: User;
}

export interface FieldTechniciansGetParams extends PagingParams {
  fullName?: string;
  orderByColumn?: "email" | "firstName" | "lastName";
  COPSMatch?: string;
}

export interface FieldTechnicianCreateParams {
  COPSMatch?: string;
  userId?: string;
}

export interface FieldTechnicianUpdateParams extends Partial<FieldTechnicianCreateParams> {
  COPSMatch?: string;
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    status?: UserStatusType | string;
    phone?: string;
    role?: string;
    lastLoginAt?: string;
    password?: string;
    settings?: {
      isEmailVerified?: boolean;
      isPhoneVerified?: boolean;
    };
    id?: string;
  };
}

export interface FieldTechnicianTableRow {
  key: string;
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  COPSMatch: string;
  status: string;
  createdAt: string;
  userId: string;
  lastLoginAt?: string;
}

export interface FieldTechniciansTableData {
  data: FieldTechnicianTableRow[];
  total: number;
}

export const useFieldTechniciansGetAll = <DD = FieldTechniciansTableData>(
  decorateData?: (data: PagingDataResponse<FieldTechnician>) => DD,
): FetchGet<
  PagingDataResponse<FieldTechnician>,
  FieldTechniciansGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("technician", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useFieldTechniciansTableGet = () =>
  useFieldTechniciansGetAll<FieldTechniciansTableData>(
    ({
      data,
      meta,
    }: PagingDataResponse<FieldTechnician>): FieldTechniciansTableData => ({
      data: data.map(
        (item: FieldTechnician): FieldTechnicianTableRow => ({
          key: item.id,
          id: item.id,
          firstName: item.user.firstName,
          lastName: item.user.lastName,
          fullName: `${item.user.firstName || "-"} ${item.user.lastName || "-"}`,
          email: item.user.email,
          phone: item.user.phone,
          COPSMatch: item.COPSMatch,
          status: item.user.status,
          createdAt: item.createdAt,
          userId: item.user.id,
          lastLoginAt: item.user.lastLoginAt || "-",
        }),
      ),
      total: meta.itemCount,
    }),
  );

export const useFieldTechnicianGetById = <DD = FieldTechnician>(
  id?: string,
  decorateData?: (data: FieldTechnician) => DD,
): FetchGetId<FieldTechnician, DefaultFetchError, unknown, DD> =>
  useFetchGetId("technician", id, {
    autoStart: !!id,
    decorateData,
  });

export const useFieldTechnicianUpdate = (
  id?: string,
): FetchUpdate<
  FieldTechnician,
  DefaultFetchError,
  FieldTechnicianUpdateParams
> => useFetchUpdate("technician", id);

export const useFieldTechnicianDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> =>
  useFetchDelete("technician", id);

export const useFieldTechniciansGet = (): FetchGet<
  PagingDataResponse<FieldTechnician>,
  FieldTechniciansGetParams
> =>
  useFetchGet("technician", {
    autoStart: false,
    startStateLoading: false,
  });

export const useFieldTechnicianCreate = (): FetchCreate<
  FieldTechnician,
  DefaultFetchError,
  FieldTechnicianCreateParams
> => useFetchCreate("technician");

export default {
  useFieldTechniciansGet,
};
